// Copy from Word or a press release often arrives with a non-breaking space
// between every word. Text like that can't wrap, so one long teaser forces a
// table-based newsletter wider than its container (CMS-7907).

// U+00A0 no-break space and U+202F narrow no-break space.
const NO_BREAK_SPACE = /[\u00A0\u202F]/g;
// Zero-width no-break space (BOM) and word joiner: invisible, and they also
// block wrapping.
const INVISIBLE_JOINERS = /[\uFEFF\u2060]/g;
const HAS_SOMETHING_TO_FIX = /[\u00A0\u202F\uFEFF\u2060]/;
const WHITESPACE = /\s/;

const BLOCK_TAGS = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'CAPTION', 'DD', 'DIV', 'DL', 'DT',
  'FIGCAPTION', 'FIGURE', 'FOOTER', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HEADER',
  'LI', 'OL', 'P', 'SECTION', 'TABLE', 'TD', 'TH', 'TR', 'UL',
]);
const SKIP_TAGS = new Set(['PRE', 'CODE', 'SCRIPT', 'STYLE', 'TEXTAREA']);

/** Plain-text fields: every no-break space becomes a normal space. */
export function normalizePastedText(text: string): string {
  return (text || '').replace(INVISIBLE_JOINERS, '').replace(NO_BREAK_SPACE, ' ');
}

interface PasteEventLike {
  clipboardData: DataTransfer | null;
  preventDefault(): void;
}

/**
 * onPaste handler for plain-text inputs and textareas. Re-inserts the pasted
 * text with normal spaces; insertText keeps undo and fires the input event, so
 * onChange still sees the change. Not for password fields: a pasted secret
 * must arrive exactly as copied.
 */
export function pasteWithNormalSpaces(e: PasteEventLike): void {
  const text = e.clipboardData?.getData('text/plain');
  if (!text) return;
  const cleaned = normalizePastedText(text);
  if (cleaned === text) return;
  if (document.execCommand && document.execCommand('insertText', false, cleaned)) {
    e.preventDefault();
  }
}

// One walk up to the root: the nearest block, and whether a pre/code/etc.
// sits anywhere above the text (highlighted code wraps lines in divs/ps).
function ancestry(node: Node, root: Node): { block: Node, skipped: boolean } {
  let skipped = false;
  let block: Node = null;
  let current = node.parentNode;
  while (current && current !== root) {
    if (SKIP_TAGS.has(current.nodeName)) skipped = true;
    if (!block && BLOCK_TAGS.has(current.nodeName)) block = current;
    current = current.parentNode;
  }
  return { block: block || root, skipped };
}

// A no-break space standing in for an ordinary word gap: something other than
// whitespace on both sides. Runs of them, one next to a normal space, and one
// at the start or end of a line (an indent) are left alone.
function isLone(text: string, i: number): boolean {
  if (i === 0 || i === text.length - 1) return false;
  return !WHITESPACE.test(text[i - 1]) && !WHITESPACE.test(text[i + 1]);
}

/**
 * Rich-text paste: swap word-gap no-break spaces for normal spaces in every
 * text node under root, in place. Neighbours are judged across inline
 * elements, so `<b>a </b>&nbsp;b` keeps its spacing. A line that holds
 * nothing but whitespace (Word's `<p>&nbsp;</p>` blank line, an empty table
 * cell) keeps its &nbsp; so it doesn't collapse, and pre/code content is not
 * touched.
 */
export function normalizePastedSpacesIn(root: Node): void {
  // Most pastes have nothing to fix; skip the walk for those.
  if (!HAS_SOMETHING_TO_FIX.test(root.textContent || '')) return;

  // Text is judged one visual line at a time: a <br>, or entering or leaving
  // a block, starts a new line, so a no-break space at the start of a line
  // still counts as an indent.
  const doc = root.ownerDocument || (root as Document);
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  const lines: { node: Text, skipped: boolean }[][] = [];
  let line: { node: Text, skipped: boolean }[] = [];
  let lastBlock: Node = null;
  const breakLine = () => {
    if (line.length) lines.push(line);
    line = [];
  };
  while (walker.nextNode()) {
    const current = walker.currentNode;
    if (current.nodeType !== Node.TEXT_NODE) {
      if (current.nodeName === 'BR' || BLOCK_TAGS.has(current.nodeName)) breakLine();
      continue;
    }
    const { block, skipped } = ancestry(current, root);
    if (block !== lastBlock) breakLine();
    lastBlock = block;
    line.push({ node: current as Text, skipped });
  }
  breakLine();

  lines.forEach((entries) => {
    if (!entries.some(({ node, skipped }) => !skipped && HAS_SOMETHING_TO_FIX.test(node.data))) return;

    // Strip the joiners first so they don't hide a neighbour.
    const parts = entries.map(({ node, skipped }) => (skipped ? node.data : node.data.replace(INVISIBLE_JOINERS, '')));
    const text = parts.join('');
    const blank = text.trim() === '';

    let offset = 0;
    entries.forEach(({ node, skipped }, index) => {
      const part = parts[index];
      const start = offset;
      offset += part.length;
      if (skipped) return;

      let next = part;
      if (!blank) {
        next = part.replace(NO_BREAK_SPACE, (match, i: number) => (isLone(text, start + i) ? ' ' : match));
      }
      if (next !== node.data) node.data = next;
    });
  });
}
